<template>
  <layout-default>
    <div
      v-if="loading"
      class="container loader-wrapper d-flex justify-content-center align-items-center"
    >
      <div class="loader"></div>
    </div>

    <div v-else class="container vendor-page">
      <div class="vendor-breadcrumb border-bottom">
        <Breadcrumb />
      </div>

      <div v-if="vendor" class="vendor-content">
        <div class="row g-5 align-items-start">
          <div class="col-lg-4">
            <form class="contact-card" @submit.prevent="handleContactVendor">
              <h2 class="contact-card__title">Contact Master Craftsmen</h2>
              <BasicInputText
                v-model="contactForm.name"
                :placeholder="'Your name'"
                :hasFocus="false"
                required
              />
              <input
                v-model="contactForm.email"
                type="email"
                class="form-control fw-light pt-4 pb-4"
                placeholder="you@email.com"
                required
              />
              <textarea
                v-model="contactForm.message"
                class="contact-card__textarea"
                placeholder="Type your message..."
                rows="5"
                required
              ></textarea>
              <ButtonFilled
                :hasRoundedCorners="true"
                class="contact-card__submit"
                :text="sendingMessage ? 'Sending…' : 'Send Message'"
              />
            </form>
            <div v-if="operationalCoordinates" class="vendor-map-card mt-4">
              <h3 class="vendor-map-card__title">Workshop Location</h3>
              <div class="vendor-map-card__map" ref="vendorMapRef"></div>
              <p v-if="operationalAddress" class="vendor-map-card__address">
                {{ operationalAddress }}
              </p>
              <!-- New Directions Link -->
              <p v-if="operationalAddress" class="vendor-map-card__directions">
                <a
                  :href="`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(operationalAddress)}`"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get Directions
                </a>
              </p>
              <!-- End of New Directions Link -->
            </div>
          </div>

          <div class="col-lg-8">
            <div class="vendor-hero">
              <div class="vendor-hero__image">
                <img
                  v-if="vendorImage"
                  :src="vendorImage"
                  :alt="vendorName"
                />
                <div v-else class="vendor-hero__image--placeholder">
                  Image unavailable
                </div>
              </div>
              <div class="vendor-hero__details">
                <h1 class="vendor-name">{{ vendorName }}</h1>
                <p v-if="vendorSubtitle" class="vendor-subtitle">
                  {{ vendorSubtitle }}
                </p>
                <p v-if="vendorBio" class="vendor-bio text-secondary">
                  {{ vendorBio }}
                </p>

                <ul class="vendor-meta">
                  <li v-if="legalAddress">
                    <span class="meta-label">Headquarters:</span>
                    <span class="meta-value">{{ legalAddress }}</span>
                  </li>
              <li v-if="operationalAddress">
                <span class="meta-label">Workshop:</span>
                <span class="meta-value">{{ operationalAddress }}</span>
              </li>
              <li v-if="vendor?.partitaIva || vendor?.raw?.partitaIva">
                <span class="meta-label">VAT</span>
                <span class="meta-value">{{ vendor?.partitaIva || vendor?.raw?.partitaIva }}</span>
              </li>
              <li v-if="vendor?.totalProducts">
                <span class="meta-label">Products</span>
                <span class="meta-value">{{ vendor.totalProducts }}</span>
              </li>
              <li v-for="contact in displayContacts" :key="contact.id">
                <span class="meta-label">{{ contact.label }}:</span>
                <a
                      v-if="contact.href"
                      :href="contact.href"
                      class="meta-link"
                      target="_blank"
                      rel="noopener"
                    >
                      {{ contact.value }}
                    </a>
                    <span v-else class="meta-value">{{ contact.value }}</span>
                  </li>
                </ul>

                <div v-if="socialLinks.length" class="vendor-socials">
                  <span class="meta-label">Follow</span>
                  <ul class="vendor-socials__list">
                    <li v-for="link in socialLinks" :key="link.id">
                      <a
                        :href="link.href"
                        class="vendor-socials__icon"
                        :class="link.className"
                        target="_blank"
                        rel="noopener"
                      >
                        <font-awesome-icon :icon="link.icon" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section class="vendor-products">
          <div class="vendor-products__header">
            <h2 class="vendor-products__title">Products</h2>
            <div class="vendor-products__actions">
              <BasicInputSearch
                class="vendor-search"
                placeholder="Enter product name"
                @onInput="searchTerm = $event"
              />
              <select
                v-model="sortOption"
                class="vendor-products__sort form-select"
              >
                <option value="default">Default sorting</option>
                <option value="price-asc">Price: low to high</option>
                <option value="price-desc">Price: high to low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="name-desc">Name: Z to A</option>
              </select>
            </div>
          </div>

          <div v-if="displayProducts.length" class="row mt-4">
            <div
              v-for="product in displayProducts"
              :key="product._id"
              class="col-12 col-sm-6 col-lg-3 mb-4"
            >
              <ProductCard
                v-bind="product"
                :endDate="product.endDate ? new Date(product.endDate) : null"
              />
            </div>
          </div>
          <div v-else class="vendor-products__empty text-center">
            <p>This vendor has no products available at the moment.</p>
          </div>
        </section>
      </div>

      <div v-else class="vendor-empty text-center py-5">
        <h2 class="mb-3">Master Craftsmen not found</h2>
        <p class="text-secondary mb-4">
          The vendor you are looking for may have been removed or is temporarily
          unavailable.
        </p>
        <ButtonFilled
          :hasRoundedCorners="true"
          text="Back to Home"
          @onClick="$router.push({ name: 'Home' })"
        />
      </div>
    </div>
  </layout-default>
</template>

<script>
import { ref, watch, computed, getCurrentInstance, onBeforeUnmount, nextTick } from "vue";
import LayoutDefault from "../../components/organisms/LayoutDefault/LayoutDefault.vue";
import Breadcrumb from "../../components/atoms/Breadcrumb/Breadcrumb.vue";
import ButtonFilled from "../../components/atoms/Button/ButtonFilled.vue";
import BasicInputText from "../../components/atoms/FormInput/BasicInputText.vue";
import BasicInputSearch from "../../components/atoms/FormInput/BasicInputSearch.vue";
import ProductCard from "../../components/molecules/Product/ProductCard.vue";
import { findVendor, fetchVendorProducts } from "../../composables/manageVendors";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Mapgeo from "@/plugins/mapgeo";

export default {
  name: "VendorView",
  components: {
    LayoutDefault,
    Breadcrumb,
    ButtonFilled,
    BasicInputText,
    BasicInputSearch,
    ProductCard,
    FontAwesomeIcon,
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const instance = getCurrentInstance();
    const globals = instance?.appContext.config.globalProperties ?? {};

    const loading = ref(true);
    const vendor = ref(null);
    const products = ref([]);
    const searchTerm = ref("");
    const sortOption = ref("default");
    const sendingMessage = ref(false);
    const contactForm = ref({
      name: "",
      email: "",
      message: "",
    });
    const vendorMapRef = ref(null);
    let vendorMapInstance = null;

    const vendorName = computed(
      () =>
        vendor.value?.denominazioneSociale ||
        vendor.value?.nome ||
        vendor.value?.name ||
        ""
    );

    const vendorSubtitle = computed(
      () =>
        vendor.value?.formaGiuridica ||
        vendor.value?.raw?.formaGiuridica ||
        ""
    );
    const vendorBio = computed(
      () =>
        vendor.value?.descrizione ||
        vendor.value?.biografia ||
        vendor.value?.bio ||
        vendor.value?.raw?.descrizione ||
        ""
    );

    const resolvePath = (path) => {
      if (!path) return "";
      if (/^https?:\/\//i.test(path)) {
        return path;
      }
      return globals.$getFileSource ? globals.$getFileSource(path) : path;
    };

    const vendorImage = computed(() =>
      resolvePath(
        vendor.value?.image ||
          vendor.value?.gallery?.[0] ||
          vendor.value?.immagine?.path ||
          vendor.value?.immagine
      )
    );

    const legalAddress = computed(() => {
      const val =
        vendor.value?.sedeLegale?.[0]?.value ||
        vendor.value?.raw?.sedeLegale?.[0]?.value;
      return val || "";
    });
    const operationalAddress = computed(() => {
      const val =
        vendor.value?.sedeOperativa?.[0]?.value ||
        vendor.value?.raw?.sedeOperativa?.[0]?.value;
      return val || "";
    });
    const operationalCoordinates = computed(() => {
      const coords =
        vendor.value?.sedeOperativa?.[0]?.loc?.coordinates ||
        vendor.value?.raw?.sedeOperativa?.[0]?.loc?.coordinates;

      if (!Array.isArray(coords) || coords.length < 2) {
        return null;
      }

      const lon = Number(coords[0]);
      const lat = Number(coords[1]);
      if (Number.isNaN(lon) || Number.isNaN(lat)) {
        return null;
      }
      return [lon, lat];
    });

    const contactIconMap = {
      mobile: ["fas", "mobile"],
      phone: ["fas", "phone"],
      email: ["fas", "envelope"],
      mail: ["fas", "envelope"],
      website: ["fas", "globe"],
      site: ["fas", "globe"],
      whatsapp: ["fab", "whatsapp"],
      facebook: ["fab", "facebook-f"],
      instagram: ["fab", "instagram"],
      linkedin: ["fab", "linkedin-in"],
      twitter: ["fab", "twitter"],
    };

    const buildContactId = (contact, index) =>
      `${contact?.type || "contact"}-${contact?.value || index}`;

    const normaliseContactValue = (contact) => {
      if (!contact) return { icon: ["fas", "phone"], value: "", label: "" };

      const type = contact.type?.toLowerCase?.() ?? "contact";
      const value = contact.value ?? "";
      const label = contact.label || type;

      let href = "";
      if (["mobile", "phone", "whatsapp"].includes(type) && value) {
        href = `tel:${value}`;
      } else if (["email", "mail"].includes(type) && value) {
        href = `mailto:${value}`;
      } else if (
        ["website", "site", "facebook", "instagram", "linkedin", "twitter"].includes(
          type
        )
      ) {
        if (value) {
          href = /^https?:\/\//i.test(value) ? value : `https://${value}`;
        }
      }

      return {
        id: buildContactId(contact, value),
        type,
        icon: contactIconMap[type] || ["fas", "phone"],
        value,
        label,
        href,
        className: type,
      };
    };

    const normalisedContacts = computed(() =>
      (vendor.value?.contatti || []).map(normaliseContactValue)
    );

    const socialLinks = computed(() =>
      normalisedContacts.value.filter((contact) =>
        ["facebook", "instagram", "linkedin", "twitter", "whatsapp"].includes(
          contact.type
        )
      )
    );

    const displayContacts = computed(() =>
      normalisedContacts.value.filter(
        (contact) =>
          !["facebook", "instagram", "linkedin", "twitter", "whatsapp"].includes(
            contact.type
          )
      )
    );

    const extractProductName = (product) =>
      product?._prodotto?.[0]?.nome || product?.nome || "";

    const extractProductDescription = (product) =>
      product?.descrizione ||
      product?._prodotto?.[0]?.descrizione ||
      "";

    const extractProductPrice = (product) => {
      const price =
        product?.prezzo ??
        product?._prodotto?.[0]?.prezzo ??
        product?.price ??
        null;
      return typeof price === "number" && Number.isFinite(price)
        ? price
        : null;
    };

    const searchableFields = (product) =>
      `${extractProductName(product)} ${extractProductDescription(product)}`.toLowerCase();

    const displayProducts = computed(() => {
      const term = searchTerm.value.trim().toLowerCase();
      let list = [...products.value];

      if (term) {
        list = list.filter((product) => searchableFields(product).includes(term));
      }

      const sorters = {
        "price-asc": (a, b) => {
          const priceA = extractProductPrice(a);
          const priceB = extractProductPrice(b);
          if (priceA === null && priceB === null) return 0;
          if (priceA === null) return 1;
          if (priceB === null) return -1;
          return priceA - priceB;
        },
        "price-desc": (a, b) => {
          const priceA = extractProductPrice(a);
          const priceB = extractProductPrice(b);
          if (priceA === null && priceB === null) return 0;
          if (priceA === null) return 1;
          if (priceB === null) return -1;
          return priceB - priceA;
        },
        "name-asc": (a, b) =>
          extractProductName(a)
            .toString()
            .localeCompare(extractProductName(b).toString()),
        "name-desc": (a, b) =>
          extractProductName(b)
            .toString()
            .localeCompare(extractProductName(a).toString()),
      };

      const sorter = sorters[sortOption.value];
      if (sorter) {
        list = [...list].sort(sorter);
      }

      return list;
    });

    const destroyVendorMap = () => {
      if (vendorMapInstance) {
        vendorMapInstance.destroy();
        vendorMapInstance = null;
      }
    };

    const renderVendorMap = () => {
      if (!vendorMapRef.value || !operationalCoordinates.value) {
        destroyVendorMap();
        return;
      }

      destroyVendorMap();

      vendorMapInstance = new Mapgeo(vendorMapRef.value, {
        variant: "italy",
        width: 520,
        height: 700,
        padding: 40,
        fillColor: "#4d4d4d",
        strokeColor: "#979797",
        strokeWidth: 1.1,
        glowColor: "rgba(0, 0, 0, 0.55)",
        markers: [
          {
            coordinates: operationalCoordinates.value,
            label: vendorName.value || "Workshop",
            info: operationalAddress.value || "Operational site",
          },
        ],
      });
    };

    watch(
      [operationalCoordinates, operationalAddress, () => vendorMapRef.value],
      () => {
        nextTick(() => {
          renderVendorMap();
        });
      },
      { immediate: true, deep: true }
    );

    onBeforeUnmount(() => {
      destroyVendorMap();
    });

    const resetContactForm = () => {
      contactForm.value = {
        name: "",
        email: "",
        message: "",
      };
    };

    const setBreadcrumb = () => {
      const vendorLocation = globals.$buildVendorURL
        ? globals.$buildVendorURL(vendor.value)
        : `/vendors/${vendor.value?.containerPath || vendor.value?._id || ""}`;

      const breadcrumb = [
        { title: "Master Craftsmen", location: "/vendors" },
        {
          title: vendorName.value,
          location: vendorLocation,
        },
      ];
      store.commit("setLocation", breadcrumb);
    };

    const loadVendor = async () => {
      loading.value = true;
      vendor.value = null;
      products.value = [];

      const identifier = route.params.vendorId;
      const fetchedVendor = await findVendor(identifier);
      console.log("fetchedVendor", fetchedVendor);
      vendor.value = fetchedVendor;

      if (fetchedVendor) {
        const vendorProducts = await fetchVendorProducts(
          fetchedVendor.containerPath || fetchedVendor._id
        );
        products.value = vendorProducts.length
          ? vendorProducts
          : fetchedVendor.products || [];

        document.title = `${vendorName.value} - Master Craftsmen - viabrera`;
        setBreadcrumb();
      } else {
        document.title = "Master Craftsmen - viabrera";
        store.commit("setLocation", [{ title: "Master Craftsmen", location: "/vendors" }]);
      }

      loading.value = false;
    };

    watch(
      () => route.params.vendorId,
      () => {
        loadVendor();
      },
      { immediate: true }
    );

    const handleContactVendor = async () => {
      if (sendingMessage.value) {
        return;
      }

      if (
        !contactForm.value.name ||
        !contactForm.value.email ||
        !contactForm.value.message
      ) {
        globals.$toast?.error?.("Please complete all fields.");
        return;
      }

      try {
        sendingMessage.value = true;
        await new Promise((resolve) => setTimeout(resolve, 600));
        globals.$toast?.success?.("Your message has been sent to the vendor.");
        resetContactForm();
      } finally {
        sendingMessage.value = false;
      }
    };

    return {
      loading,
      vendor,
      vendorName,
      vendorSubtitle,
      vendorBio,
      vendorImage,
      legalAddress,
      operationalAddress,
      socialLinks,
      displayContacts,
      displayProducts,
      searchTerm,
      sortOption,
      sendingMessage,
      contactForm,
      operationalCoordinates,
      vendorMapRef,
      handleContactVendor,
    };
  },
};
</script>

<style scoped>
.vendor-page {
  padding: 48px 0 120px;
}

.vendor-breadcrumb {
  padding-bottom: 32px;
  margin-bottom: 32px;
}

.vendor-content {
  display: flex;
  flex-direction: column;
  gap: 64px;
}

.contact-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.08);
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.contact-card input {
  border: 1px solid #dfe5e9;
  border-radius: 6px;
  padding: 12px 14px !important;
}

.contact-card input:focus {
  border-color: #000;
  box-shadow: none;
}

.contact-card__title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
}

.contact-card__textarea {
  border: 1px solid #dfe5e9;
  border-radius: 6px;
  padding: 12px 14px;
  resize: vertical;
  min-height: 140px;
}

.contact-card__textarea:focus {
  outline: none;
  border-color: #000;
}

.contact-card__submit {
  align-self: flex-start;
  padding-left: 24px;
  padding-right: 24px;
}

.vendor-map-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.08);
  padding: 24px;
}

.vendor-map-card__title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
}

.vendor-map-card__map {
  width: 100%;
  height: 320px;
  border-radius: 16px;
  overflow: hidden;
  background: #0b1724;
}

.vendor-map-card__address {
  margin-top: 12px;
  font-size: 0.95rem;
  color: #555;
}

.vendor-hero {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.vendor-hero__image {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.18);
}

.vendor-hero__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.vendor-hero__image--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 0;
  background: #f7f7f7;
  color: #9b9b9b;
  font-size: 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.vendor-hero__details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.vendor-name {
  font-size: 34px;
  font-weight: 700;
  margin: 0;
}

.vendor-subtitle {
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: #8c8c8c;
  margin-bottom: 0;
}

.vendor-bio {
  font-size: 16px;
  line-height: 1.7;
}

.vendor-meta {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.meta-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #8c8c8c;
  margin-right: 8px;
}

.meta-value {
  font-weight: 600;
  color: #1d1d1d;
}

.meta-link {
  font-weight: 600;
  color: #1d1d1d;
  text-decoration: none;
}

.meta-link:hover {
  color: var(--primary-color);
}

.vendor-socials {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
}

.vendor-socials__list {
  display: flex;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.vendor-socials__icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #ffffff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.vendor-socials__icon:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.15);
}

.vendor-socials__icon.facebook {
  background: #4064ac;
}

.vendor-socials__icon.instagram {
  background: #e1306c;
}

.vendor-socials__icon.linkedin {
  background: #2867b2;
}

.vendor-socials__icon.twitter {
  background: #1da1f2;
}

.vendor-socials__icon.whatsapp {
  background: #25d366;
}

.vendor-products {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.vendor-products__header {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}

.vendor-products__title {
  font-size: 26px;
  font-weight: 700;
  margin: 0;
}

.vendor-products__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}

.vendor-products__sort {
  width: 200px;
}

.vendor-products__empty {
  padding: 40px 0;
  font-size: 16px;
  color: #6f6f6f;
}

.vendor-empty {
  margin-top: 60px;
}

@media (max-width: 991.98px) {
  .vendor-page {
    padding-bottom: 80px;
  }

  .contact-card {
    padding: 24px 20px;
  }

  .vendor-name {
    font-size: 28px;
  }
}

@media (max-width: 575.98px) {
  .vendor-breadcrumb {
    padding-bottom: 20px;
    margin-bottom: 24px;
  }

  .vendor-products__sort {
    width: 100%;
  }

  .vendor-socials {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

<style>
  .loader-wrapper {
    min-width: 100vw;
    min-height: 100vh;
  }
  .loader-wrapper .loader {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 100px auto;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
